import { defineStore } from "pinia";
import { ref } from "vue";
import {
  BarcodeType,
  DomainObjectPropertyType,
  LabelHeightInMm,
  LabelObjectOrientation,
  LabelObjectPlainTextPosition,
  LabelTemplateDestinationType,
  LabelTemplateDPIResolution,
  type LabelTemplateDTO,
  LabelTemplateObjectAlignmentType,
  type LabelTemplateObjectDTO,
  LabelTemplateObjectType,
  LabelTemplateTextType,
  LabelWidthInMm,
} from "@/api/classes/labelTemplate/labelTemplate.dto";
import type { BaseProductDto } from "@/types/product/productTypes";
import {
  DomainObjectPropertyTypeTranslation,
  LabelObjectOrientationTranslations,
  LabelObjectPlainTextPositionTranslations,
  LabelTemplateBarcodeTypeTranslation,
  LabelTemplateDestinationTypeTranslation,
  LabelTemplateDPIResolutionTranslation,
  LabelTemplateObjectAlignmentTypeTranslation,
  LabelTemplateObjectTypeTranslation,
  LabelTemplateTextTypeTranslation,
} from "@/api/classes/labelTemplate/labelTemplateTranslates";
import { usePartner } from "@/stores/partner/partner";
import { createNotification } from "@/tools/notification";
import { useLabelTemplateStore } from "@/stores/labelTemplate/labelTemplateStore";
import Product from "@/api/classes/product/product";
import { initialFilter } from "@/stores/product/products/productsTable";
import axios from "axios";

const createTranslatesFromEnum = (translates: Record<string, string>) => {
  return Object.entries(translates).map(([value, label]) => ({
    label,
    value: Number(value),
  }));
};

export const useLabelTemplateFormStore = defineStore(
  "labelTemplateFormStore",
  () => {
    const partnerStore = usePartner();
    const labelTemplateStore = useLabelTemplateStore();

    const currentLabelTemplateObjects = ref<LabelTemplateObjectDTO[]>([]);
    const selectedLabelTemplateObject = ref<string | null>(null);
    const currentEditingLabel = ref<string>("");

    const textFontFieldOptions = [
      "Arial",
      "Helvetica",
      "Verdana",
      "Tahoma",
      "Courier New",
      "Times New Roman",
    ].map((value) => ({ label: value, value }));
    const domainObjectPropertyTypeOptions = createTranslatesFromEnum(
      DomainObjectPropertyTypeTranslation,
    );
    const labelTemplateDestinationTypeOptions = createTranslatesFromEnum(
      LabelTemplateDestinationTypeTranslation,
    );
    const labelTemplateDPIResolutionOptions = createTranslatesFromEnum(
      LabelTemplateDPIResolutionTranslation,
    );
    const labelTemplateObjectTypeOptions = createTranslatesFromEnum(
      LabelTemplateObjectTypeTranslation,
    );
    const barcodeTypeOptions = createTranslatesFromEnum(
      LabelTemplateBarcodeTypeTranslation,
    );
    const labelTemplateObjectAlignmentTypeOptions = createTranslatesFromEnum(
      LabelTemplateObjectAlignmentTypeTranslation,
    );
    const labelTemplateTextTypeOptions = createTranslatesFromEnum(
      LabelTemplateTextTypeTranslation,
    );
    const labelObjectOrientationOptions = createTranslatesFromEnum(
      LabelObjectOrientationTranslations,
    );
    const labelObjectPlainTextPositionOptions = createTranslatesFromEnum(
      LabelObjectPlainTextPositionTranslations,
    );

    const nameField = ref<string>("Новая этикетка");
    const labelHeightInMmField = ref<LabelHeightInMm>(LabelHeightInMm.SMALL);
    const labelWidthInMmField = ref<LabelWidthInMm>(LabelWidthInMm.SMALL);
    const labelTemplateDestinationTypeField = ref<LabelTemplateDestinationType>(
      LabelTemplateDestinationType.ProductItemFBM,
    );
    const previewProductField = ref<BaseProductDto["id"] | null>(null);
    const domainObjectPropertyTypeField = ref<DomainObjectPropertyType[]>([]);
    const barcodeTypeField = ref<BarcodeType>(BarcodeType.EAN13);
    const displayBarcodeTextField = ref<string>("");
    const barcodeHeightInMmField = ref<number>(10);
    const barcodeSizeField = ref<number>(1);
    const labelTemplateDPIResolutionField = ref<LabelTemplateDPIResolution>(
      LabelTemplateDPIResolution.ThreeHundred,
    );
    const countOfCopiesField = ref<number>(1);
    const labelTemplateObjectTypeField = ref<LabelTemplateObjectType>(
      LabelTemplateObjectType.Text,
    );
    const labelTemplateTextTypeField = ref<LabelTemplateTextType>(
      LabelTemplateTextType.PlainText,
    );
    const topMarginField = ref<number>(5);
    const rightMarginField = ref<number>(5);
    const leftMarginField = ref<number>(5);
    const bottomMarginField = ref<number>(5);
    const alignmentTypeField = ref<LabelTemplateObjectAlignmentType>(
      LabelTemplateObjectAlignmentType.Centered,
    );
    const plainTextField = ref<string>("Текст");
    const plainTextPositionField = ref<number>(
      LabelObjectPlainTextPosition.Left,
    );
    const textFontField = ref<string>(textFontFieldOptions[0].value);
    const textSizeField = ref<number>(12);
    const numberOfMaximumNumberOfLinesField = ref<number>(3);
    const enableLineBreaksField = ref<boolean>(true);
    const labelObjectOrientationField = ref<LabelObjectOrientation>(
      LabelObjectOrientation.Horizontal,
    );

    const resetFieldsWithDefaultState = (resetMainFields: boolean = false) => {
      labelTemplateObjectTypeField.value = LabelTemplateObjectType.Text;
      labelTemplateTextTypeField.value = LabelTemplateTextType.PlainText;
      topMarginField.value = 5;
      rightMarginField.value = 5;
      leftMarginField.value = 5;
      bottomMarginField.value = 5;
      alignmentTypeField.value = LabelTemplateObjectAlignmentType.Centered;
      plainTextField.value = "Текст";
      plainTextPositionField.value = LabelObjectPlainTextPosition.Left;
      textFontField.value = textFontFieldOptions[0].value;
      textSizeField.value = 12;
      numberOfMaximumNumberOfLinesField.value = 3;
      enableLineBreaksField.value = true;
      labelObjectOrientationField.value = LabelObjectOrientation.Horizontal;
      domainObjectPropertyTypeField.value = [];
      barcodeTypeField.value = BarcodeType.EAN13;
      displayBarcodeTextField.value = "";
      barcodeHeightInMmField.value = 10;
      barcodeSizeField.value = 1;

      if (resetMainFields) {
        nameField.value = "Новая этикетка";
        labelHeightInMmField.value = LabelHeightInMm.SMALL;
        labelWidthInMmField.value = LabelWidthInMm.SMALL;
        previewProductField.value = null;
        labelTemplateDestinationTypeField.value =
          LabelTemplateDestinationType.ProductItemFBM;
        labelTemplateDPIResolutionField.value =
          LabelTemplateDPIResolution.ThreeHundred;
        countOfCopiesField.value = 1;
      }
    };

    const createLabelTemplateObjectFromFormState =
      (): LabelTemplateObjectDTO => {
        return {
          objectType: labelTemplateObjectTypeField.value,
          alignmentType: alignmentTypeField.value,
          textType: labelTemplateTextTypeField.value,
          plainText: plainTextField.value,
          domainObjectPropertyTypes: domainObjectPropertyTypeField.value,
          enableLineBreaks: enableLineBreaksField.value,
          numberOfMaximumNumberOfLines: numberOfMaximumNumberOfLinesField.value,
          textSize: textSizeField.value,
          textFont: textFontField.value,
          condition: "", // нужно ли это поле вообще
          topMargin: topMarginField.value,
          leftMargin: leftMarginField.value,
          bottomMargin: bottomMarginField.value,
          rightMargin: rightMarginField.value,
          barcodeType: barcodeTypeField.value,
          displayBarcodeText: displayBarcodeTextField.value,
          barcodeHeightInMm: barcodeHeightInMmField.value,
          barcodeSize: barcodeSizeField.value,
          labelObjectOrientation: labelObjectOrientationField.value,
          plainTextPosition: plainTextPositionField.value,
        };
      };

    const createLabelTemplateFromState = (): LabelTemplateDTO | null => {
      if (!canCreateLabelTemplate()) return null;

      const isDefault = !labelTemplateStore.allPersonalLabels.some(
        (label) =>
          label.destinationType === labelTemplateDestinationTypeField.value,
      );

      const existingLabel = labelTemplateStore.allPersonalLabels.find(
        (label) => label.id === currentEditingLabel.value,
      );

      return {
        partnerId: partnerStore.partner!.id,
        isDefault,
        countOfCopies: countOfCopiesField.value,
        destinationType: labelTemplateDestinationTypeField.value,
        labelTemplateName: nameField.value,
        labelDPIResolution: labelTemplateDPIResolutionField.value,
        labelWidthInMm: labelWidthInMmField.value,
        labelHeightInMm: labelHeightInMmField.value,
        imageWidth: 0,
        imageHeight: 0, // TODO внести реальные значения
        productForPreview: previewProductField.value || null,
        labelTemplateObjects: currentLabelTemplateObjects.value,
        attachedPrinterName: existingLabel
          ? existingLabel.attachedPrinterName
          : null,
        id: currentEditingLabel.value,
      };
    };

    const canCreateLabelTemplate = () => {
      const noPartnerMessage = "Не найден id партнера";
      let message: string = "";

      if (!partnerStore.partner) {
        message = noPartnerMessage;
      }

      if (message) {
        createNotification("error", "Ошибка", message);

        return false;
      }

      return true;
    };

    const fillFields = async (label: LabelTemplateDTO) => {
      await fillMainFields(label);
      currentLabelTemplateObjects.value = label.labelTemplateObjects;

      if (currentLabelTemplateObjects.value?.length) {
        selectedLabelTemplateObject.value = getLabelTemplateObjectStringId(
          label.labelTemplateObjects[0],
          0,
        );
      }
    };

    const fillMainFields = async (label: LabelTemplateDTO) => {
      let product: BaseProductDto | null = null;

      if (label.productForPreview) {
        try {
          const { data } = await Product.getBaseProduct(
            label.productForPreview,
          );

          product = data;
        } catch (e: unknown) {
          onError(
            e,
            "Не удалось найти товар для предпросмотра, использован товар по умолчанию",
          );
        }
      }

      if (!product) {
        try {
          const { data } = await Product.getProductsForTable(1, 0, {
            ...initialFilter,
          });
          product = Object.values(data.item1).flat()[0];
        } catch (e: unknown) {
          onError(e, "Не удалось получить товары");
        }
      }

      if (product) {
        labelTemplateStore.productsForPreviewOptions = [
          ...labelTemplateStore.productsForPreviewOptions,
          { value: product.id, label: product.title },
        ];

        labelTemplateStore.productForPreviewMap.set(product.id, product);
        previewProductField.value = product.id;
      }

      currentLabelTemplateObjects.value = label.labelTemplateObjects || [];
      nameField.value = label.labelTemplateName;
      labelTemplateDestinationTypeField.value = label.destinationType;
      labelWidthInMmField.value = label.labelWidthInMm;
      labelHeightInMmField.value = label.labelHeightInMm;
      previewProductField.value = product?.id || null;
      labelTemplateDPIResolutionField.value = label.labelDPIResolution;
      countOfCopiesField.value = label.countOfCopies;
    };

    const selectLabelTemplateObject = (object: LabelTemplateObjectDTO) => {
      labelTemplateObjectTypeField.value = object.objectType;
      alignmentTypeField.value = object.alignmentType;
      labelTemplateTextTypeField.value = object.textType;
      plainTextField.value = object.plainText;
      domainObjectPropertyTypeField.value = object.domainObjectPropertyTypes;
      enableLineBreaksField.value = object.enableLineBreaks;
      numberOfMaximumNumberOfLinesField.value =
        object.numberOfMaximumNumberOfLines;
      textSizeField.value = object.textSize;
      textFontField.value = object.textFont;
      topMarginField.value = object.topMargin;
      leftMarginField.value = object.leftMargin;
      bottomMarginField.value = object.bottomMargin;
      rightMarginField.value = object.rightMargin;
      barcodeTypeField.value = object.barcodeType;
      displayBarcodeTextField.value = object.displayBarcodeText;
      barcodeHeightInMmField.value = object.barcodeHeightInMm;
      barcodeSizeField.value = object.barcodeSize;
      labelObjectOrientationField.value = object.labelObjectOrientation;
      plainTextPositionField.value = object.plainTextPosition;
    };

    const resetFullState = () => {
      resetFieldsWithDefaultState(true);
      currentLabelTemplateObjects.value = [];
      currentEditingLabel.value = "";
      selectedLabelTemplateObject.value = null;
    };

    const getLabelTemplateObjectStringId = (
      item: LabelTemplateObjectDTO,
      index: number,
    ) => {
      return `${item.objectType}_${index}`;
    };

    const getLabelTemplateObjectDataFromStringId = (data: string) => {
      const [_, index] = data.split("_");

      return currentLabelTemplateObjects.value[+index];
    };

    const saveSelectedLabelTemplateObject = () => {
      if (!selectedLabelTemplateObject.value) return;

      const labelObject = createLabelTemplateObjectFromFormState();

      currentLabelTemplateObjects.value = currentLabelTemplateObjects.value.map(
        (item, index) => {
          const value = getLabelTemplateObjectStringId(item, index);

          return value === selectedLabelTemplateObject.value
            ? labelObject
            : item;
        },
      );
    };

    const deleteLabelTemplateObject = (value: string) => {
      const [_, index] = value.split("_");

      currentLabelTemplateObjects.value =
        currentLabelTemplateObjects.value.filter(
          (_, itemIndex) => itemIndex !== +index,
        );

      if (selectedLabelTemplateObject.value === value) {
        const firstItem = currentLabelTemplateObjects.value[0];

        selectedLabelTemplateObject.value = firstItem
          ? getLabelTemplateObjectStringId(firstItem, 0)
          : null;
      }
    };

    const addLabelTemplate = (item?: LabelTemplateObjectDTO) => {
      let labelTemplateObject = item;

      if (!labelTemplateObject) {
        labelTemplateObject = {
          objectType: LabelTemplateObjectType.Text,
          alignmentType: LabelTemplateObjectAlignmentType.Centered,
          textType: LabelTemplateTextType.PlainText,
          plainText: "Текст",
          domainObjectPropertyTypes: [],
          enableLineBreaks: true,
          numberOfMaximumNumberOfLines: 3,
          textSize: 12,
          textFont: textFontFieldOptions[0].value,
          condition: "", // нужно ли это поле вообще
          topMargin: 5,
          leftMargin: 5,
          bottomMargin: 5,
          rightMargin: 5,
          barcodeType: BarcodeType.EAN13,
          displayBarcodeText: "",
          barcodeHeightInMm: 10,
          barcodeSize: 1,
          labelObjectOrientation: LabelObjectOrientation.Horizontal,
          plainTextPosition: LabelObjectPlainTextPosition.Left,
        };
      }

      currentLabelTemplateObjects.value = [
        ...currentLabelTemplateObjects.value,
        labelTemplateObject,
      ];

      selectedLabelTemplateObject.value = getLabelTemplateObjectStringId(
        labelTemplateObject,
        currentLabelTemplateObjects.value.length - 1,
      );
    };

    const onError = (err: unknown, customMessage?: string) => {
      if (axios.isAxiosError(err)) {
        console.log(err);
        createNotification(
          "error",
          customMessage || err.response?.data?.detail || err.response?.data,
          undefined,
          "topRight",
        );
      }
    };

    return {
      textFontFieldOptions,
      domainObjectPropertyTypeOptions,
      labelTemplateDestinationTypeOptions,
      labelTemplateDPIResolutionOptions,
      labelTemplateObjectTypeOptions,
      labelTemplateObjectAlignmentTypeOptions,
      labelTemplateTextTypeOptions,
      labelObjectOrientationOptions,
      labelObjectPlainTextPositionOptions,
      nameField,
      labelHeightInMmField,
      labelWidthInMmField,
      labelTemplateDestinationTypeField,
      previewProductField,
      domainObjectPropertyTypeField,
      barcodeTypeField,
      displayBarcodeTextField,
      barcodeTypeOptions,
      barcodeHeightInMmField,
      barcodeSizeField,
      labelTemplateDPIResolutionField,
      countOfCopiesField,
      labelTemplateObjectTypeField,
      labelTemplateTextTypeField,
      topMarginField,
      rightMarginField,
      leftMarginField,
      bottomMarginField,
      alignmentTypeField,
      plainTextField,
      plainTextPositionField,
      textFontField,
      textSizeField,
      numberOfMaximumNumberOfLinesField,
      enableLineBreaksField,
      selectedLabelTemplateObject,
      labelObjectOrientationField,
      currentLabelTemplateObjects,
      currentEditingLabel,
      resetFieldsWithDefaultState,
      createLabelTemplateObjectFromFormState,
      createLabelTemplateFromState,
      canCreateLabelTemplate,
      resetFullState,
      fillFields,
      getLabelTemplateObjectStringId,
      getLabelTemplateObjectDataFromStringId,
      deleteLabelTemplateObject,
      saveSelectedLabelTemplateObject,
      selectLabelTemplateObject,
      addLabelTemplate,
    };
  },
);
